import { RoundsComparison } from "src/app/domain/portfolio-comparison/dto/RoundsComparison";
import { Rows } from "../../interfaces";
import { RoundsComparisonExportExcelDTO } from "./round-comparison-template";
import { dashInsteadOfNull, dashInsteadOfNullCommon, formats, getCell, getStatusForSubTotal, withCellStyles } from "../../utils";
import { boldLightGrayBgLeft, left, leftBold } from "src/app/util/excelStyles";
import { boldLightGrayBg } from "../financial-statement/capital-account/styles";
import { boldGrayBgLeft } from "../comparison/mapping";

export const getPreparedData = (data: RoundsComparisonExportExcelDTO,
  headersForLogic: string[], headersForDisplay: string[], firsRow: string[]) => {
  const newData: Rows = [];
  totalIndexArray = [];
  // start row index
  currentRowIndex = 3;
  // adding first row
  newData.push(firsRow);
  let row = [];
  headersForDisplay.forEach((header) => (header === '' ? row.push(null) : row.push(withCellStyles(header, boldLightGrayBg))));
  // adding headers
  newData.push(row);
  const active = data.activeRounds;
  const sold = data.soldRounds;

  setRoundsRow(newData, 'Active', headersForLogic, data.activeRounds);
  setRoundsRow(newData, 'Sold', headersForLogic, data.soldRounds);

  if (active.length && sold.length) {
    const grandTotalRow = getGrandTotalRow(headersForLogic);
    newData.push(...grandTotalRow);
  }
  totalIndexArray = [];
  return newData;
};

export const setRoundsRow = (newData: Rows, status: string, headersForLogic: string[], rounds: RoundsComparison[]) => {
  if (rounds?.length) {
    const row = [];
    row.push(withCellStyles(status.toLowerCase() === 'Sold' ? 'Realized' : status, leftBold));
    newData.push(row);
    currentRowIndex++;
    const activeRows = getRoundsRows(rounds, status, headersForLogic);
    newData.push(...activeRows);
    const totalActiveRows = getTotalRow(status, headersForLogic, 4);
    newData.push(...totalActiveRows);
    currentRowIndex++;
    return newData;
  }
  return;
}

export const getRoundsRows = (rounds: RoundsComparison[], status: string, headers: string[]) => {
  const rows: Rows = [];
  const mappedToHeaders = rounds.map((round) => getNewRoundComparisonMapping(round));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], left));
    }
    rows.push(row);
  }
  return rows;
};

export const getTotalRow = (status: string, headers: string[], startRowIndexForTotal: number) => {
  const rows: Rows = [];
  totalIndexArray.push(currentRowIndex);
  const mappedToHeaders = getTotalMapping(status, startRowIndexForTotal);
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldLightGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

export const getGrandTotalRow = (headers: string[]) => {
  const rows: Rows = [];
  totalIndexArray.push(currentRowIndex);
  const mappedToHeaders = getGrandTotalMapping();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

export const getTotalMapping = (status: string, startRowIndexForTotal: number) => {
  const body = {
    'Round Name': 'Total ' + getStatusForSubTotal(status),
    'empty(0)': null,
    'Investment(0)': getCell({
      f: `Sum(C${startRowIndexForTotal}:C${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(0)': getCell({
      f: `Sum(D${startRowIndexForTotal}:D${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)(0)': getCell({
      f: `Sum(E${startRowIndexForTotal}:E${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Funds Shares(0)': getCell({
      f: `Sum(F${startRowIndexForTotal}:F${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Valuated PPS(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding(0)': getCell({
      f: `Sum(H${startRowIndexForTotal}:H${currentRowIndex - 1})`,
      z: formats.percents,
      t: 'n',
    }),
    'empty(1)': null,
    'Investment(1)': getCell({
      f: `Q${currentRowIndex}-C${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(1)': getCell({
      f: `R${currentRowIndex}-D${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)(1)': getCell({
      f: `K${currentRowIndex}-J${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Funds Shares(1)': getCell({
      f: `T${currentRowIndex}-F${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Valuated PPS(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding(1)': getCell({
      f: `V${currentRowIndex}-H${currentRowIndex}`,
      z: formats.percents,
      t: 'n',
    }),
    'empty(2)': null,
    'Investment(2)': getCell({
      f: `Sum(Q${startRowIndexForTotal}:Q${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(2)': getCell({
      f: `Sum(R${startRowIndexForTotal}:R${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)(2)': getCell({
      f: `Sum(S${startRowIndexForTotal}:S${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Funds Shares(2)': getCell({
      f: `Sum(T${startRowIndexForTotal}:T${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Valuated PPS(2)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding(2)': getCell({
      f: `Sum(V${startRowIndexForTotal}:V${currentRowIndex - 1})`,
      z: formats.percents,
      t: 'n',
    }),
  };
  return [body];
};

export const getNewRoundComparisonMapping = (r: RoundsComparison) => {
  const body = {
    'Round Name': r.roundName,
    'empty(0)': null,
    'Investment(0)': getCell({
      v: r.costSecond ,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(0)': getCell({
      v: r.fmvSecond,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)(0)': getCell({
      f: `D${currentRowIndex}-C${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Funds Shares(0)': getCell({
      v: r.fundSharesSecond,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Valuated PPS(0)': getCell({
      v: dashInsteadOfNullCommon(r.ppsSecond),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding(0)': getCell({
      v: r.holdingSecond,
      z: formats.percents,
      t: 'n',
    }),
    'empty(1)': null,
    'Investment(1)': getCell({
      f: `Q${currentRowIndex}-C${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(1)': getCell({
      f: `R${currentRowIndex}-D${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)(1)': getCell({
      f: `K${currentRowIndex}-J${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Funds Shares(1)': getCell({
      f: `T${currentRowIndex}-F${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Valuated PPS(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding(1)': getCell({
      f: `V${currentRowIndex}-H${currentRowIndex}`,
      z: formats.percents,
      t: 'n',
    }),
    'empty(2)': null,
    'Investment(2)': getCell({
      v: r.costFirst,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(2)': getCell({
      v: r.fmvFirst,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)(2)': getCell({
      f: `R${currentRowIndex}-Q${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Funds Shares(2)': getCell({
      v: r.fundSharesFirst,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Valuated PPS(2)': getCell({
      v: dashInsteadOfNullCommon(r.ppsFirst),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding(2)': getCell({
      v: r.holdingFirst,
      z: formats.percents,
      t: 'n',
    }),
  };
  currentRowIndex++;
  return body;
};

export const getGrandTotalMapping = () => {
  const totalActiveIndex = totalIndexArray[0];
  const totalSoldIndex = totalIndexArray[1];
  const body = {
    'Round Name': 'Grand total',
    'empty(0)': null,
    'Investment(0)': getCell({
      f: `Sum(C${totalActiveIndex},C${totalSoldIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(0)': getCell({
      f: `Sum(D${totalActiveIndex},D${totalSoldIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)(0)': getCell({
      f: `Sum(E${totalActiveIndex},E${totalSoldIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Funds Shares(0)': getCell({
      f: `Sum(F${totalActiveIndex},F${totalSoldIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Valuated PPS(0)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding(0)': getCell({
      f: `Sum(H${totalActiveIndex},H${totalSoldIndex})`,
      z: formats.percents,
      t: 'n',
    }),
    'empty(1)': null,
    'Investment(1)': getCell({
      f: `Q${currentRowIndex}-C${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(1)': getCell({
      f: `R${currentRowIndex}-D${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)(1)': getCell({
      f: `K${currentRowIndex}-J${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Funds Shares(1)': getCell({
      f: `T${currentRowIndex}-F${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Valuated PPS(1)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding(1)': getCell({
      f: `K${currentRowIndex}-J${currentRowIndex}`,
      z: formats.percents,
      t: 'n',
    }),
    'empty(2)': null,
    'Investment(2)': getCell({
      f: `Sum(Q${totalActiveIndex},Q${totalSoldIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(2)': getCell({
      f: `Sum(R${totalActiveIndex},R${totalSoldIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Gain/(loss)(2)': getCell({
      f: `Sum(S${totalActiveIndex},S${totalSoldIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Funds Shares(2)': getCell({
      f: `Sum(T${totalActiveIndex},T${totalSoldIndex})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Valuated PPS(2)': getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Holding(2)': getCell({
      f: `Sum(V${totalActiveIndex},V${totalSoldIndex})`,
      z: formats.percents,
      t: 'n',
    }),
  };
  return [body];
};

export let currentRowIndex = 3;

export let totalIndexArray = [];
